<template>
  <div>
    <!-- Backdrop for mobile drawer -->
    <div
      v-if="isMobile && mobileDrawerOpen"
      class="mobile-backdrop"
      @click="closeMobileDrawer"
    ></div>

    <!-- Transparent overlay to block hover after closing floor plan dialog -->
    <div
      v-if="hoverBlocked"
      class="hover-block-overlay"
    ></div>

    <!-- Sidebar -->
    <aside
      class="sidebar"
      :class="{
        'mobile-drawer': isMobile,
        'mobile-drawer-open': isMobile && mobileDrawerOpen
      }"
    >
      <!-- Logo section — samo na desktopu, na mobilnom je logo u TopBaru -->
      <div v-if="!isMobile" class="sidebar-logo-section" @click="$router.push({ name: 'companies' }).catch(e => { if (e.name !== 'NavigationDuplicated') throw e })">
        <img src="/logo.png" alt="Logo" class="sidebar-logo-img" />
      </div>
      <div class="sidebar-content">
      <!-- Scroll Up Indicator -->
      <div
        v-if="canScrollUp"
        class="scroll-indicator scroll-up"
        @click="scrollUp"
      >
        <i class="mdi mdi-chevron-double-up"></i>
      </div>

      <!-- Navigation -->
      <nav class="nav-section" ref="navSection" @scroll="checkScrollability">
        <div
          v-for="(item, idx) in items"
          :key="item.id || item.title + idx"
          class="nav-item-wrapper"
        >
          <!-- Divider -->
          <div v-if="item.divider" class="nav-divider"></div>

          <!-- Dialog item (not used in current implementation, but kept for compatibility) -->
          <div
            v-else-if="item.dialog && !item.hidden"
            class="nav-item"
            @click="item.action"
          >
            <i
              v-if="item.icon"
              class="mdi nav-icon"
              :class="item.icon"
              :style="{ background: item.iconBg || '', color: item.iconColor || '' }"
            ></i>
            <div class="nav-label">{{ item.title }}</div>
          </div>

          <!-- Item with children (menu) -->
          <div
            v-else-if="item.menu && !item.hidden"
            @mouseenter="handleMouseEnter(item.id || item.title + idx, $event)"
            @mouseleave="handleMouseLeave"
            @click="handleMenuClick(item.id || item.title + idx, $event)"
          >
            <div class="nav-item" :class="{ active: isSubmenuActive(item) }">
              <i
                v-if="item.icon"
                class="mdi nav-icon"
                :class="item.icon"
                :style="{ background: item.iconBg || '', color: item.iconColor || '' }"
              ></i>
              <div class="nav-label">{{ item.title }}</div>
            </div>

            <!-- Submenu Panel -->
            <div
              v-if="menuState.activeSubmenu === (item.id || item.title + idx)"
              class="submenu-panel"
              :class="{ 'submenu-panel--columns': item.columns }"
              :style="item.columns ? getColumnPanelStyle() : {}"
              @mouseenter="handleSubmenuEnter"
              @mouseleave="handleSubmenuLeave"
            >
              <!-- header namjerno sakriven -->

              <!-- Multi-column layout -->
              <div v-if="item.columns" class="submenu-columns">
                <div
                  v-for="(col, ci) in item.columns"
                  v-show="col.items.some(c => !c.hidden)"
                  :key="ci"
                  class="submenu-column"
                >
                  <div v-if="col.title" class="submenu-column-title">{{ col.title }}</div>
                  <div class="submenu-column-items">
                    <router-link
                      v-for="(child, i) in col.items"
                      v-show="!child.hidden"
                      :key="child.title + i"
                      :to="child.disabled ? '#' : getRouteWithParams(child.to)"
                      class="submenu-item"
                      :class="{
                        active: isSubmenuItemActive(child),
                        disabled: child.disabled
                      }"
                      @click.native="handleSubmenuItemClick(child, $event)"
                    >
                      <div
                        v-if="child.icon"
                        class="submenu-icon-badge"
                        :style="child.iconBg ? { background: child.iconBg } : {}"
                      >
                        <i
                          class="mdi submenu-icon"
                          :class="child.icon"
                          :style="child.iconColor ? { color: child.iconColor } : {}"
                        ></i>
                      </div>
                      <div class="submenu-label-wrap">
                        <div class="submenu-label" :data-text="child.title">{{ child.title }}</div>
                        <div v-if="child.subtitle" class="submenu-subtitle">{{ child.subtitle }}</div>
                      </div>
                    </router-link>
                  </div>
                </div>
              </div>

              <!-- Single-column layout (original) -->
              <div v-else class="submenu-items">
                <template v-for="(child, i) in item.children">
                  <!-- Separator -->
                  <div
                    v-if="child.separator && !child.hidden"
                    :key="'separator-' + i"
                    class="submenu-separator"
                  >
                    <div v-if="child.label" class="submenu-separator-label">
                      {{ child.label }}
                    </div>
                    <div class="submenu-separator-line"></div>
                  </div>

                  <!-- Regular submenu item -->
                  <router-link
                    v-else-if="!child.hidden"
                    :key="child.title + i"
                    :to="child.disabled ? '#' : getRouteWithParams(child.to)"
                    class="submenu-item"
                    :class="{
                      active: isSubmenuItemActive(child),
                      disabled: child.disabled
                    }"
                    @click.native="handleSubmenuItemClick(child, $event)"
                  >
                    <div
                      v-if="child.icon"
                      class="submenu-icon-badge"
                      :style="child.iconBg ? { background: child.iconBg } : {}"
                    >
                      <i
                        class="mdi submenu-icon"
                        :class="child.icon"
                        :style="child.iconColor ? { color: child.iconColor } : {}"
                      ></i>
                    </div>
                    <div class="submenu-label" :data-text="child.title">{{ child.title }}</div>
                  </router-link>
                </template>
              </div>
            </div>
          </div>

          <!-- Simple item without children -->
          <router-link
            v-else-if="!item.menu && !item.dialog && !item.hidden && item.to"
            :to="getRouteWithParams(item.to)"
            class="nav-item"
            :class="{ active: isItemActive(item) }"
            @click.native="setActiveMenu(idx)"
          >
            <i v-if="item.icon" class="mdi nav-icon" :class="item.icon" :style="{ background: item.iconBg || '', color: item.iconColor || '' }"></i>
            <div class="nav-label">{{ item.title }}</div>
          </router-link>
        </div>
      </nav>

      <!-- Scroll Down Indicator -->
      <div
        v-if="canScrollDown"
        class="scroll-indicator scroll-down"
        @click="scrollDown"
      >
        <i class="mdi mdi-chevron-double-down"></i>
      </div>

      <!-- User Section -->
      <div class="user-section">
        <button
          @click="logOut"
          class="logout-item"
          :title="$t('$vuetify.formDetails.logOut')"
        >
          <i class="mdi logout-icon mdi-logout"></i>
          <div class="logout-label">
            {{ $t("$vuetify.formDetails.logOut") }}
          </div>
        </button>
      </div>
    </div>
  </aside>
  </div>
</template>

<script>
import state from '@/state'
import applicationSettings from '@/mixins/applicationSettings'
import logOutUser from '@/mixins/logOutUser'
import { df, auth } from '@/plugins/firebase'
import { v4 as uuidv4 } from 'uuid'
import menuStateManager from '@/state/menuState'
import EventBus from '@/plugins/event-bus'

export default {
  mixins: [applicationSettings, logOutUser],
  data: () => ({
    drawerOne: false,
    drawerExpanded: false,
    expand: false,
    items: [],
    drawerKey: 1,
    mini: true,
    companySelected: false,
    warehouseModuleAction: 'Aktiviraj modul skladište',
    menuState: menuStateManager.getState(),
    hoverTimeout: null,
    canScrollUp: false,
    canScrollDown: false,
    eracunActive: false,
    hoverBlocked: false,
    hoverUnblockTimeout: null,
    activeSubmenuAnchorTop: null,
    activeSubmenuAnchorBottom: null,
    columnPanelTop: 0
  }),
  computed: {
    drawerModel: {
      set () { },
      get () {
        return (
          this.$vuetify.breakpoint.lgAndUp ||
          (this.drawerOne && this.$vuetify.breakpoint.mdAndDown)
        )
      }
    },
    sideSize () {
      let ret = 240
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
          ret = '80%'
          break
        case 'sm':
          ret = '80%'
          break
        case 'md':
          ret = 250
          break
        case 'lg': {
          ret = 250
        }
      }
      return ret
    },
    currentCompany () {
      return state.getCurrentCompany()
    },
    mobileDrawerOpen () {
      return state.drawer
    },
    isMobile () {
      return this.$vuetify.breakpoint.smAndDown
    }
  },
  watch: {
    $route (to) {
      this.setRouteData(to.meta.sideNav)
      // Zatvori drawer na mobilnim uređajima pri promjeni rute
      this.closeMobileDrawer()
      // Update eracun status when route changes (e.g. switching companies)
      const company = state.getCurrentCompany()
      this.eracunActive = company?.eracun?.active === true
    },
    // Watch for changes in eracun status
    eracunActive (newVal) {
      // Force re-render of menu items when eracun status changes
      this.setRouteData(this.$route.meta.sideNav)
    },
    drawerOne (nv) {
      if (nv === true && this.mini === true) {
        this.drawerOne = false
      }

      state.setDrawerOne(this.drawerOne)

      this.expand = !nv
    },
    mini (nv) {
      if (nv === true && this.drawerOne === true) {
        this.drawerOne = false
      }
    }
  },
  methods: {
    closeMobileDrawer () {
      if (this.isMobile) {
        state.setDrawer(false)
      }
    },
    getRouteWithParams (to) {
      // Ako to nije objekt ili nema name, vrati ga kao što je
      if (!to || typeof to !== 'object' || !to.name) {
        return to
      }

      // Kreiraj novi route objekt sa trenutnim params
      const routeWithParams = { ...to }

      // Ako route nema definirane params, kopiraj ih iz trenutne rute
      if (!routeWithParams.params) {
        routeWithParams.params = { ...this.$route.params }
      } else {
        // Ako ima params, dodaj samo one koji nedostaju
        routeWithParams.params = {
          ...this.$route.params,
          ...routeWithParams.params
        }
      }

      return routeWithParams
    },
    isItemActive (item) {
      const currentRoute = this.$route

      // Posebna logika za Master meni - ostaje aktivan za sve all-reports rute
      if (item.id === 'menu-master') {
        return currentRoute.name && currentRoute.name.includes('all-reports')
      }

      // Standardna logika - provjeri da li je trenutna ruta
      if (item.to && item.to.name) {
        return currentRoute.name === item.to.name
      }

      return false
    },
    isSubmenuActive (item) {
      const allItems = item.columns
        ? item.columns.flatMap(col => col.items)
        : (item.children || [])
      if (!allItems.length) return false
      const currentRoute = this.$route
      return allItems.some(child => {
        if (child.id === 'menu-master') {
          return currentRoute.name && currentRoute.name.includes('all-reports')
        }
        if (child.to && child.to.name) {
          return currentRoute.name === child.to.name
        }
        return false
      })
    },
    isSubmenuItemActive (child) {
      const currentRoute = this.$route

      // Posebna logika za Master - ostaje aktivan za sve all-reports rute
      if (child.id === 'menu-master') {
        return currentRoute.name && currentRoute.name.includes('all-reports')
      }

      // Standardna logika - provjeri da li je trenutna ruta
      if (child.to && child.to.name) {
        return currentRoute.name === child.to.name
      }

      return false
    },
    handleSubmenuItemClick (child, event) {
      // Prevent navigation if item is disabled
      if (child.disabled) {
        event.preventDefault()
        return
      }

      // Ako postoji akcija za promjenu modula, izvrši je
      if (child.changeModule) {
        this.setWarehouseModule()
      }

      // Zatvori drawer na mobilnim uređajima nakon klika
      this.closeMobileDrawer()
      // Menu state se čuva kroz navigaciju
    },
    handleMenuClick (itemId, event) {
      if (menuStateManager.isSubmenuOpen(itemId)) {
        menuStateManager.closeSubmenu()
        return
      }
      if (event) {
        const rect = event.currentTarget.getBoundingClientRect()
        this.activeSubmenuAnchorTop = rect.top
        this.activeSubmenuAnchorBottom = rect.bottom
      }
      menuStateManager.openSubmenu(itemId)
      this._positionColumnPanel()
    },
    handleMouseEnter (itemId, event) {
      if (this.hoverBlocked) return

      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout)
        this.hoverTimeout = null
      }
      if (event) {
        const rect = event.currentTarget.getBoundingClientRect()
        this.activeSubmenuAnchorTop = rect.top
        this.activeSubmenuAnchorBottom = rect.bottom
      }
      menuStateManager.openSubmenu(itemId)
      this._positionColumnPanel()
    },
    _positionColumnPanel () {
      if (this.isMobile) return
      this.$nextTick(() => {
        const panel = this.$el.querySelector('.submenu-panel--columns')
        if (!panel || this.activeSubmenuAnchorTop === null) return
        const margin = 8
        const vh = window.innerHeight
        const h = panel.offsetHeight
        this.columnPanelTop = Math.max(margin, Math.min(this.activeSubmenuAnchorTop, vh - h - margin))
      })
    },
    getColumnPanelStyle () {
      if (this.isMobile) return {}
      return { top: `${this.columnPanelTop}px`, bottom: 'auto' }
    },
    blockHoverTemporarily () {
      // Blokiraj hover dok korisnik ne pomakne miša
      this.hoverBlocked = true

      // Zatvori trenutno otvoreni submenu
      menuStateManager.closeSubmenu()

      // Očisti postojeći timeout ako postoji
      if (this.hoverUnblockTimeout) {
        clearTimeout(this.hoverUnblockTimeout)
        this.hoverUnblockTimeout = null
      }

      // Dodaj mousemove listener da ukloni blokadu nakon što se miš pomakne
      const handleMouseMove = () => {
        // Ne uklanjaj odmah blokadu, čekaj još 250ms nakon pokreta miša
        this.hoverUnblockTimeout = setTimeout(() => {
          this.hoverBlocked = false
          this.hoverUnblockTimeout = null
        }, 250)

        // Ukloni listener odmah (želimo samo prvi pokret)
        document.removeEventListener('mousemove', handleMouseMove)
      }

      document.addEventListener('mousemove', handleMouseMove, { once: true })
    },
    handleMouseLeave () {
      this.hoverTimeout = setTimeout(() => {
        if (!this.menuState.isMouseOverSubmenu) {
          menuStateManager.closeSubmenu()
        }
      }, 200)
    },
    handleSubmenuEnter () {
      menuStateManager.setMouseOverSubmenu(true)
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout)
        this.hoverTimeout = null
      }
    },
    handleSubmenuLeave () {
      menuStateManager.setMouseOverSubmenu(false)
      this.hoverTimeout = setTimeout(() => {
        menuStateManager.closeSubmenu()
      }, 300)
    },
    checkScrollability () {
      const navSection = this.$refs.navSection
      if (!navSection) return

      // Provjeri da li se može skrolati gore
      this.canScrollUp = navSection.scrollTop > 5

      // Provjeri da li se može skrolati dolje
      const scrollBottom = navSection.scrollHeight - navSection.scrollTop - navSection.clientHeight
      this.canScrollDown = scrollBottom > 5
    },
    scrollUp () {
      const navSection = this.$refs.navSection
      if (!navSection) return

      // Skrolaj gore za visinu jednog nav itema (otprilike 60px)
      navSection.scrollBy({
        top: -60,
        behavior: 'smooth'
      })
    },
    scrollDown () {
      const navSection = this.$refs.navSection
      if (!navSection) return

      // Skrolaj dolje za visinu jednog nav itema (otprilike 60px)
      navSection.scrollBy({
        top: 60,
        behavior: 'smooth'
      })
    },
    openSetupWizard () {
      this.$emit('open-setup-wizard')
    },
    setWarehouseModule () {
      const that = this

      let newWarehouse = 'basic'
      switch (state.getCurrentCompany().warehouse) {
        case 'basic':
          newWarehouse = 'module'
          break
        case 'module':
          newWarehouse = 'basic'
          break
      }

      try {
        const reqId = uuidv4()

        const payload = {
          action: {
            operation: 'set',
            entity: 'warehouse_module',
            resource_id: state.getCurrentCompany().id,
            params: {
              warehouse: newWarehouse
            }
          }
        }

        df.doc(`request/${reqId}`)
          .set({
            user_id: `${auth.currentUser.uid}`,
            device_id: 'web',
            created: `${new Date().getTime()}`,
            type: 'warehouse',
            payload: btoa(
              unescape(encodeURIComponent(JSON.stringify(payload)))
            )
          })
          .then(function (doc) {
          })
          .catch(function (error) {
            // console.error('Error writing document: ', error)
            that.showMsgBox({
              text: error || 'Internal error',
              actions: ['cancel'],
              cancelBtnText: 'OK',
              color: 'error'
            })
            that.submitting = false
          })
          .finally(() => { })
      } catch (err) {
        this.showMsgBox({
          text: err && err !== '' ? err : 'An error has occurred',
          actions: ['cancel'],
          cancelBtnText: 'OK',
          color: 'error'
        })
        this.submitting = false
      }
    },
    setMenuStatus () {
      if (this.mini) {
        this.items.forEach(item => {
          if (item.active) {
            item.active = false
            item._active = true
          }
        })
      } else {
        this.items.forEach(item => {
          if (item._active) {
            item.active = true
            item._active = false
          }
        })
      }
      this.$forceUpdate()
    },
    setDrawerExpandedState () {
      if (!this.mini) {
        this.drawerExpanded = true
      } else {
        this.drawerExpanded = false
      }
      this.$forceUpdate()
    },
    setActiveMenu (itemIndex) {
      this.items = this.items.map((item, id) => {
        if (id === itemIndex) {
          item.active = true
        } else {
          item.active = false
        }
        return item
      })
    },

    setRouteData (sideNav) {
      const usersTabVisible =
        !!(state.getUser()?.roles?.SUPERADMIN || state.getUser()?.roles?.ADMIN)

      const comp = state.getCurrentCompany()
      let hideOldWarehouse = false

      if (comp.active_products !== undefined) {
        Object.keys(comp.active_products).forEach((id) => {
          if (id === '232a3f0b-999a-4742-ab1a-5094264e6c24' && comp.active_products[id].active === true) {
            hideOldWarehouse = true
          }
        })
      }

      const adminKnjigovođa = state.getUser()?.roles?.SUPERADMIN || state.getUser()?.roles?.KNJIGOVOĐA || state.getUser()?.roles?.ADMIN
      const isSuperAdmin = state.getUser()?.roles?.SUPERADMIN
      const isPausalnObrt = comp.type === 'pausalni_obrt'

      let targetModifier = ''

      switch (sideNav) {
        case 'Companies':
          break
        case 'Company':
          targetModifier = 'company'
          break
        case 'PointOfSale':
          targetModifier = 'pointOfSale'
          break
        case 'CashRegister':
          targetModifier = 'pointOfSale'
          break
      }

      var userNotSupport = true
      if (auth.currentUser.uid === 'Zg4qIpxX2uNGKvjznSVnICZAVtJ2') {
        userNotSupport = false
      }

      if (process.env.VUE_APP_API_ROOT === 'https://europe-west1-ai-fisk-stage.cloudfunctions.net/') {
        userNotSupport = false
      }

      const sideNavItems = [
        {
          // WIZARD - Postavljanje nove firme
          id: 'menu-setup-wizard',
          appearsOn: [
            'Companies'
          ],
          title: 'Postavi novu firmu',
          icon: 'mdi-wizard-hat',
          iconColor: '#6A1B9A',
          iconBg: 'rgba(106, 27, 154, 0.15)',
          dialog: true,
          action: () => this.openSetupWizard()
        },
        {
          // ADMIN
          id: 'menu-admin',
          appearsOn: [
            // 'Companies',
            'Company',
            'PointOfSale',
            'CashRegister'
          ],
          title: this.$t('$vuetify.cashRegisterTabs.admin'),
          icon: 'mdi-account-group-outline',
          iconColor: '#37474F',
          iconBg: 'rgba(55, 71, 79, 0.15)',
          group: 'items',
          hidden: userNotSupport,
          menu: true,
          columns: [
            {
              items: [
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.pointsOfSaleTabs.companySettings'),
                  icon: 'mdi-note-text',
                  iconColor: '#1565C0',
                  iconBg: 'rgba(21, 101, 192, 0.1)',
                  to: { name: `${targetModifier}.admin.companySettings` }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.pointsOfSaleTabs.companyAdd'),
                  icon: 'mdi-food-variant',
                  iconColor: '#2E7D32',
                  iconBg: 'rgba(46, 125, 50, 0.1)',
                  to: { name: `${targetModifier}.admin.companyAdd` }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.pointsOfSaleTabs.companyWarehouseModule'),
                  icon: 'mdi-warehouse',
                  iconColor: '#00838F',
                  iconBg: 'rgba(0, 131, 143, 0.1)',
                  to: { name: `${targetModifier}.admin.companyWarehouseModule` }
                }
              ]
            }
          ]
        },
        {
          // FIRME — premješteno u header breadcrumb
          id: 'menu-companies',
          appearsOn: [
            'Companies',
            'Company',
            'PointOfSale',
            'CashRegister'
          ],
          hidden: true,
          title: 'Firme',
          icon: 'mdi-briefcase-outline',
          iconColor: '#1565C0',
          iconBg: 'rgba(21, 101, 192, 0.15)',
          to: { name: 'companies' }
        },
        {
          // PRODAJNA MJESTA — premješteno u header breadcrumb
          id: 'menu-points-of-sale',
          appearsOn: [
            'Company',
            'PointOfSale',
            'CashRegister'
          ],
          hidden: true,
          title: this.$t('$vuetify.cashRegisterTabs.pointsOfSale'),
          icon: 'mdi-store',
          iconColor: '#EF6C00',
          iconBg: 'rgba(239, 108, 0, 0.15)',
          to: { name: 'company.pointsOfSale' }
        },
        {
          // BLAGAJNE — premješteno u header breadcrumb
          id: 'menu-registers',
          appearsOn: [
            'PointOfSale',
            'CashRegister'
          ],
          hidden: true,
          title: this.$t('$vuetify.cashRegisterTabs.registers'),
          icon: 'mdi-cash-register',
          iconColor: '#F9A825',
          iconBg: 'rgba(249, 168, 37, 0.15)',
          to: { name: `${targetModifier}.cashRegisters` }
        },
        {
          // RAČUNI
          id: 'menu-receipts',
          appearsOn: [
            'Company',
            'PointOfSale',
            'CashRegister'
          ],
          title: 'Računi',
          icon: 'mdi-receipt-text',
          iconColor: '#1565C0',
          iconBg: 'rgba(21, 101, 192, 0.15)',
          menu: true,
          columns: [
            {
              items: [
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: 'Računi i ponude',
                  icon: 'mdi-receipt-text',
                  iconColor: '#1565C0',
                  iconBg: 'rgba(21, 101, 192, 0.1)',
                  to: { name: `${targetModifier}.receipts` }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: 'Ulazni eRačuni',
                  icon: 'mdi-arrow-bottom-left-thick',
                  iconColor: '#2E7D32',
                  iconBg: 'rgba(46, 125, 50, 0.1)',
                  group: 'incoming-einvoices',
                  to: { name: `${targetModifier}.incoming-einvoices` },
                  hidden: false,
                  isEracunItem: true
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: 'Izlazni eRačuni',
                  icon: 'mdi-arrow-top-right-thick',
                  iconColor: '#C62828',
                  iconBg: 'rgba(198, 40, 40, 0.1)',
                  group: 'outgoing-einvoices',
                  to: { name: `${targetModifier}.outgoing-einvoices` },
                  hidden: false,
                  isEracunItem: true
                }
              ]
            }
          ]
        },
        {
          id: 'menu-reports',
          appearsOn: [
            'Company',
            'PointOfSale',
            'CashRegister'
          ],
          title: this.$t('$vuetify.cashRegisterTabs.reports'),
          icon: 'mdi-window-maximize',
          iconColor: '#6A1B9A',
          iconBg: 'rgba(106, 27, 154, 0.15)',
          menu: { title: 'meni' },
          hidden: !adminKnjigovođa,
          columns: [
            {
              items: [
                {
                  id: 'menu-master',
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.pointsOfSaleTabs.master'),
                  icon: 'mdi-shield-crown-outline',
                  iconColor: '#6A1B9A',
                  iconBg: 'rgba(106, 27, 154, 0.1)',
                  to: { name: `${targetModifier}.all-reports.reportMenu` },
                  hidden: !adminKnjigovođa
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: 'Automatsko slanje',
                  icon: 'mdi-email-fast-outline',
                  iconColor: '#6A1B9A',
                  iconBg: 'rgba(106, 27, 154, 0.1)',
                  hidden: !adminKnjigovođa,
                  to: { name: `${targetModifier}.all-reports.automatski-slanje` }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.cashRegisterTabs.closeBalanceTitles'),
                  icon: 'mdi-book-open-variant',
                  iconColor: '#37474F',
                  iconBg: 'rgba(55, 71, 79, 0.1)',
                  disabled: sideNav === 'Company',
                  subtitle: sideNav === 'Company' ? 'Odaberite prodajno mjesto' : undefined,
                  to: { name: `${targetModifier}.closeBalance.displayCloseBalance` }
                },
                {
                  appearsOn: isPausalnObrt ? ['Company', 'PointOfSale', 'CashRegister'] : [],
                  title: this.$t('$vuetify.cashRegisterTabs.posdReport'),
                  icon: 'mdi-form-select',
                  iconColor: '#1565C0',
                  iconBg: 'rgba(21, 101, 192, 0.1)',
                  to: { name: `${targetModifier}.reports.reportPosd` }
                },
                {
                  appearsOn: isPausalnObrt ? ['Company', 'PointOfSale', 'CashRegister'] : [],
                  title: this.$t('$vuetify.cashRegisterTabs.kprReport'),
                  icon: 'mdi-book-open-variant',
                  iconColor: '#2E7D32',
                  iconBg: 'rgba(46, 125, 50, 0.1)',
                  disabled: sideNav === 'Company',
                  subtitle: sideNav === 'Company' ? 'Odaberite prodajno mjesto' : undefined,
                  to: { name: 'pointOfSale.reports.reportKpr' }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.pointsOfSaleTabs.groupageColection'),
                  icon: 'mdi-book-open-variant',
                  iconColor: '#EF6C00',
                  iconBg: 'rgba(239, 108, 0, 0.1)',
                  to: { name: `${targetModifier}.all-reports.groupageCollection` }
                }
              ]
            }
          ]
        },
        {
          // ARTIKLI
          id: 'menu-items',
          appearsOn: [
            'Company',
            'PointOfSale',
            'CashRegister'
          ],
          title: this.$t('$vuetify.companyTabs.items'),
          icon: 'mdi-package-variant',
          iconColor: '#2E7D32',
          iconBg: 'rgba(46, 125, 50, 0.15)',
          group: 'items',
          menu: true,
          columns: [
            {
              items: [
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.pointsOfSaleTabs.lager'),
                  icon: 'mdi-note-text',
                  iconColor: '#EF6C00',
                  iconBg: 'rgba(239, 108, 0, 0.1)',
                  group: 'lager',
                  to: { name: `${targetModifier}.items.lager` }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.pointsOfSaleTabs.categories'),
                  icon: 'mdi-food-variant',
                  iconColor: '#AD1457',
                  iconBg: 'rgba(173, 20, 87, 0.1)',
                  group: 'categories',
                  to: { name: `${targetModifier}.items.categories` }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.uploadItems.titleText'),
                  icon: 'mdi-upload',
                  iconColor: '#283593',
                  iconBg: 'rgba(40, 53, 147, 0.1)',
                  group: 'items-upload',
                  to: { name: `${targetModifier}.items.upload` }
                },
                {
                  appearsOn: hideOldWarehouse ? ['Company', 'PointOfSale', 'CashRegister'] : [],
                  title: this.$t('$vuetify.companyTabs.norm'),
                  icon: 'mdi-clipboard-list',
                  iconColor: '#2E7D32',
                  iconBg: 'rgba(46, 125, 50, 0.1)',
                  group: 'norm',
                  to: { name: `${targetModifier}.items.norm` }
                },
                {
                  appearsOn: hideOldWarehouse ? ['Company', 'PointOfSale', 'CashRegister'] : [],
                  title: this.$t('$vuetify.restaurant.menu'),
                  icon: 'mdi-food-fork-drink',
                  iconColor: '#6A1B9A',
                  iconBg: 'rgba(106, 27, 154, 0.1)',
                  group: 'restarant-menu',
                  to: { name: `${targetModifier}.restaurant.menuItems` }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.declarations.titleText'),
                  icon: 'mdi-clipboard-text-outline',
                  iconColor: '#00838F',
                  iconBg: 'rgba(0, 131, 143, 0.1)',
                  group: 'declarations',
                  to: { name: `${targetModifier}.items.declarations` }
                }
              ]
            }
          ]
        },
        {
          // CJENIK
          id: 'menu-pricelist',
          appearsOn: [
            'Company',
            'PointOfSale',
            'CashRegister'
          ],
          title: this.$t('$vuetify.cashRegisterTabs.priceList'),
          icon: 'mdi-currency-eur',
          iconColor: '#00695C',
          iconBg: 'rgba(0, 105, 92, 0.15)',
          menu: true,
          columns: [
            {
              items: [
                {
                  appearsOn: ['PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.cashRegisterTabs.priceList'),
                  icon: 'mdi-currency-eur',
                  iconColor: '#00695C',
                  iconBg: 'rgba(0, 105, 92, 0.1)',
                  to: { name: `${targetModifier}.priceList` }
                },
                {
                  appearsOn: ['Company'],
                  title: this.$t('$vuetify.cashRegisterTabs.priceList'),
                  icon: 'mdi-currency-eur',
                  iconColor: '#00695C',
                  iconBg: 'rgba(0, 105, 92, 0.1)',
                  subtitle: 'Odaberite prodajno mjesto',
                  disabled: true,
                  to: '#'
                },
                {
                  appearsOn: ['PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.pointsOfSaleTabs.discounts'),
                  icon: 'mdi-percent',
                  iconColor: '#F9A825',
                  iconBg: 'rgba(249, 168, 37, 0.1)',
                  to: { name: `${targetModifier}.items.discount` }
                },
                {
                  appearsOn: ['Company'],
                  title: this.$t('$vuetify.pointsOfSaleTabs.discounts'),
                  icon: 'mdi-percent',
                  iconColor: '#F9A825',
                  iconBg: 'rgba(249, 168, 37, 0.1)',
                  subtitle: 'Odaberite prodajno mjesto',
                  disabled: true,
                  to: '#'
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: 'KPD Šifre',
                  icon: 'mdi-barcode',
                  iconColor: '#37474F',
                  iconBg: 'rgba(55, 71, 79, 0.1)',
                  group: 'kpd-sifra',
                  to: { name: `${targetModifier}.items.kpd` }
                }
              ]
            }
          ]
        },
        {
          // SKLADISTE
          id: 'menu-warehouse',
          appearsOn: [
            'Company',
            'PointOfSale',
            'CashRegister'
          ],
          hidden: !hideOldWarehouse,
          title: this.$t('$vuetify.warehouse.titleText'),
          icon: 'mdi-warehouse',
          iconColor: '#00838F',
          iconBg: 'rgba(0, 131, 143, 0.15)',
          menu: { title: 'meni' },
          columns: [
            {
              items: [
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.pointsOfSaleTabs.warehouseItems'),
                  icon: 'mdi-form-select',
                  iconColor: '#EF6C00',
                  iconBg: 'rgba(239, 108, 0, 0.1)',
                  to: { name: `${targetModifier}.warehouseModule.items` }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.pointsOfSaleTabs.primka'),
                  icon: 'mdi-book-open-variant',
                  iconColor: '#1565C0',
                  iconBg: 'rgba(21, 101, 192, 0.1)',
                  to: { name: `${targetModifier}.warehouseModule.documentsv2` }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: 'Kalkulacija',
                  icon: 'mdi-calculator-variant',
                  iconColor: '#F9A825',
                  iconBg: 'rgba(249, 168, 37, 0.1)',
                  to: { name: `${targetModifier}.warehouseModule.kalkulacija` }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.pointsOfSaleTabs.warehouseDocumentsOverview'),
                  icon: 'mdi-book-open-variant',
                  iconColor: '#37474F',
                  iconBg: 'rgba(55, 71, 79, 0.1)',
                  to: { name: `${targetModifier}.warehouseModule.warehouseDocumentsOverview` }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.pointsOfSaleTabs.inventory'),
                  icon: 'mdi-clipboard-list-outline',
                  iconColor: '#2E7D32',
                  iconBg: 'rgba(46, 125, 50, 0.1)',
                  to: { name: `${targetModifier}.warehouseModule.inventories` }
                },
                {
                  appearsOn: [],
                  title: 'Proizvodnja',
                  icon: 'mdi-factory',
                  iconColor: '#00695C',
                  iconBg: 'rgba(0, 105, 92, 0.1)',
                  to: { name: `${targetModifier}.production` }
                },
                {
                  appearsOn: [],
                  title: 'Proizvodnja dan',
                  icon: 'mdi-flask',
                  iconColor: '#283593',
                  iconBg: 'rgba(40, 53, 147, 0.1)',
                  to: { name: `${targetModifier}.normativeProduction` }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: 'Ispravak normativa',
                  icon: 'mdi-tune',
                  iconColor: '#C62828',
                  iconBg: 'rgba(198, 40, 40, 0.1)',
                  disabled: sideNav === 'Company',
                  subtitle: sideNav === 'Company' ? 'Odaberite prodajno mjesto' : undefined,
                  to: { name: `${targetModifier}.warehouseModule.ispravakNormativa` }
                }
              ]
            }
          ]
        },
        {
          // PRODAJNO MJESTO
          id: 'menu-warehouse-old',
          appearsOn: [
            'PointOfSale',
            'CashRegister'
          ],
          hidden: hideOldWarehouse,
          title: this.$t('$vuetify.warehouse.titleText2'),
          icon: 'mdi-warehouse',
          iconColor: '#00838F',
          iconBg: 'rgba(0, 131, 143, 0.15)',
          to: { name: `${targetModifier}.warehouse` }
        },
        {
          // NARUDŽBE
          id: 'menu-orders',
          appearsOn: [
            'Company',
            'PointOfSale',
            'CashRegister'
          ],
          hidden: !hideOldWarehouse || true, // Sakriveno
          title: this.$t('$vuetify.orders.titleText'),
          icon: 'mdi-order-bool-descending-variant',
          iconColor: '#AD1457',
          iconBg: 'rgba(173, 20, 87, 0.15)',
          menu: true,
          columns: [
            {
              items: [
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.orders.createOrder'),
                  icon: 'mdi-border-color',
                  iconColor: '#1565C0',
                  iconBg: 'rgba(21, 101, 192, 0.1)',
                  to: { name: `${targetModifier}.orders.createOrder` }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.orders.showOrders'),
                  icon: 'mdi-book-open-variant',
                  iconColor: '#2E7D32',
                  iconBg: 'rgba(46, 125, 50, 0.1)',
                  to: { name: `${targetModifier}.orders.showOrders` }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.company.suppliers'),
                  icon: 'mdi-human-dolly',
                  iconColor: '#EF6C00',
                  iconBg: 'rgba(239, 108, 0, 0.1)',
                  to: { name: `${targetModifier}.suppliers` }
                }
              ]
            }
          ]
        },
        {
          // RADNI NALOG
          id: 'menu-work-orders',
          appearsOn: [
            'PointOfSale',
            'CashRegister'
          ],
          hidden: !hideOldWarehouse,
          title: this.$t('$vuetify.workorder.titleText'),
          icon: 'mdi-file-chart-check-outline',
          iconColor: '#283593',
          iconBg: 'rgba(40, 53, 147, 0.15)',
          menu: { title: 'meni' },
          columns: [
            {
              items: [
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.pointsOfSaleTabs.workOrders'),
                  icon: 'mdi-pencil-outline',
                  iconColor: '#283593',
                  iconBg: 'rgba(40, 53, 147, 0.1)',
                  to: { name: `${targetModifier}.workOrders` }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.pointsOfSaleTabs.workOrderStaff'),
                  icon: 'mdi-account-multiple-plus-outline',
                  iconColor: '#00838F',
                  iconBg: 'rgba(0, 131, 143, 0.1)',
                  to: { name: `${targetModifier}.workOrder.staff` }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.pointsOfSaleTabs.workOrderFields'),
                  icon: 'mdi-focus-field-horizontal',
                  iconColor: '#AD1457',
                  iconBg: 'rgba(173, 20, 87, 0.1)',
                  to: { name: `${targetModifier}.workOrder.fields` }
                }
              ]
            }
          ]
        },
        {
          // KLIJENTI
          id: 'menu-associates',
          appearsOn: [
            'Company',
            'PointOfSale',
            'CashRegister'
          ],
          title: this.$t('$vuetify.pointsOfSaleTabs.associates'),
          icon: 'mdi-account-multiple-outline',
          iconColor: '#00838F',
          iconBg: 'rgba(0, 131, 143, 0.15)',
          to: { name: `${targetModifier}.associates` }
        },
        {
          // POSTAVKE
          id: 'menu-settings',
          visible: usersTabVisible,
          appearsOn: [
            'Company',
            'PointOfSale',
            'CashRegister'
          ],
          title: this.$t('$vuetify.userDataTabs.userData'),
          icon: 'mdi-cog',
          iconColor: '#37474F',
          iconBg: 'rgba(55, 71, 79, 0.15)',
          menu: true,
          columns: [
            {
              title: 'Opće',
              items: [
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: 'Prodajna mjesta i blagajne',
                  icon: 'mdi-view-column-outline',
                  iconColor: '#00695C',
                  iconBg: 'rgba(0, 105, 92, 0.1)',
                  hidden: !usersTabVisible,
                  to: { name: targetModifier ? `${targetModifier}.businessSettings` : 'businessSettings' }
                },
                {
                  appearsOn: ['Companies', 'Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.companyTabs.users'),
                  icon: 'mdi-account-multiple-outline',
                  iconColor: '#00838F',
                  iconBg: 'rgba(0, 131, 143, 0.1)',
                  hidden: !usersTabVisible,
                  to: { name: targetModifier ? `${targetModifier}.users` : 'users' }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: 'Komunikacija',
                  icon: 'mdi-email-box',
                  iconColor: '#6A1B9A',
                  iconBg: 'rgba(106, 27, 154, 0.1)',
                  hidden: !usersTabVisible,
                  to: { name: targetModifier ? `${targetModifier}.communicationConfiguration` : 'communicationConfiguration' }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: 'Fiskalizacija i E-račun',
                  icon: 'mdi-file-document-check-outline',
                  iconColor: '#C62828',
                  iconBg: 'rgba(198, 40, 40, 0.1)',
                  hidden: !usersTabVisible,
                  to: { name: targetModifier ? `${targetModifier}.fiscalizationEracun` : 'fiscalizationEracun' }
                }
              ]
            },
            {
              title: 'Prodajno mjesto',
              items: [
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: 'Printeri',
                  icon: 'mdi-printer-settings',
                  iconColor: '#37474F',
                  iconBg: 'rgba(55, 71, 79, 0.1)',
                  hidden: !usersTabVisible,
                  disabled: sideNav === 'Company',
                  subtitle: sideNav === 'Company' ? 'Odaberite prodajno mjesto' : undefined,
                  to: { name: targetModifier ? `${targetModifier}.printerConfiguration` : 'printerConfiguration' }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: 'Ustrojstvena jedinica',
                  icon: 'mdi-animation',
                  iconColor: '#EF6C00',
                  iconBg: 'rgba(239, 108, 0, 0.1)',
                  hidden: !usersTabVisible,
                  disabled: sideNav === 'Company',
                  subtitle: sideNav === 'Company' ? 'Odaberite prodajno mjesto' : undefined,
                  to: { name: targetModifier ? `${targetModifier}.organizationalUnitConfiguration` : 'organizationalUnitConfiguration' }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: 'QR Kod Meni',
                  icon: 'mdi-qrcode',
                  iconColor: '#AD1457',
                  iconBg: 'rgba(173, 20, 87, 0.1)',
                  disabled: sideNav === 'Company',
                  subtitle: sideNav === 'Company' ? 'Odaberite prodajno mjesto' : undefined,
                  to: { name: 'pointOfSale.userData.qrMenu' }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: this.$t('$vuetify.floorPlan.title'),
                  icon: 'mdi-floor-plan',
                  iconColor: '#2E7D32',
                  iconBg: 'rgba(46, 125, 50, 0.1)',
                  hidden: !usersTabVisible,
                  disabled: sideNav === 'Company',
                  subtitle: sideNav === 'Company' ? 'Odaberite prodajno mjesto' : undefined,
                  to: { name: targetModifier ? `${targetModifier}.floorPlanSettings` : 'floorPlanSettings' }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: 'Orderman',
                  icon: 'mdi-tablet',
                  iconColor: '#283593',
                  iconBg: 'rgba(40, 53, 147, 0.1)',
                  hidden: !usersTabVisible,
                  disabled: sideNav === 'Company',
                  subtitle: sideNav === 'Company' ? 'Odaberite prodajno mjesto' : undefined,
                  to: { name: targetModifier ? `${targetModifier}.orderman` : 'orderman' }
                }
              ]
            },
            {
              title: 'Kartično plaćanje',
              items: [
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: 'Platne kartice',
                  icon: 'mdi-credit-card-settings-outline',
                  iconColor: '#F9A825',
                  iconBg: 'rgba(249, 168, 37, 0.1)',
                  hidden: !usersTabVisible,
                  to: { name: targetModifier ? `${targetModifier}.cardConfiguration` : 'cardConfiguration' }
                },
                {
                  appearsOn: ['Company', 'PointOfSale', 'CashRegister'],
                  title: 'POS uređaji',
                  icon: 'mdi-account-credit-card-outline',
                  iconColor: '#00695C',
                  iconBg: 'rgba(0, 105, 92, 0.1)',
                  hidden: !usersTabVisible,
                  to: { name: targetModifier ? `${targetModifier}.posConfiguration` : 'posConfiguration' }
                }
              ]
            }
          ]
        }
      ]

      this.items = sideNavItems.filter(i => {
        return i.appearsOn.includes(sideNav) && (i.visible === undefined || i.visible)
      })
      this.items.forEach(element => {
        if (element.menu) {
          if (element.columns) {
            element.columns.forEach(col => {
              col.items = col.items.filter(c => c.appearsOn.includes(sideNav))
              col.items.forEach(child => {
                if (child.isEracunItem) {
                  child.disabled = !this.eracunActive
                  child.subtitle = !this.eracunActive ? 'Konfigurirajte postavke za eRačun' : undefined
                }
              })
            })
          } else if (element.children) {
            const filteredMenu = element.children.filter(c => {
              return c.appearsOn.includes(sideNav)
            })
            element.children = [...filteredMenu]
            element.children.forEach(child => {
              if (child.isEracunItem) {
                child.disabled = !this.eracunActive
                child.subtitle = !this.eracunActive ? 'Konfigurirajte postavke za eRačun' : undefined
              }
            })
          }
        }
      })

      // Provjeri scroll mogućnosti nakon promjene items-a
      requestAnimationFrame(() => requestAnimationFrame(() => this.checkScrollability()))
    }
  },
  created () {
  },
  mounted () {
    if (this.$route.name !== 'companies') {
      this.companySelected = true
    }

    if (state.getCurrentCompany().warehouse !== undefined && state.getCurrentCompany().warehouse !== '' && state.getCurrentCompany().warehouse === 'module') {
      this.warehouseModuleAction = 'Deaktiviraj modul skladište'
    }

    // Initialize eracun active status
    const company = state.getCurrentCompany()
    this.eracunActive = company?.eracun?.active === true

    // Listen for eracun status changes
    this.$root.$on('eracun-status-changed', (isActive) => {
      this.eracunActive = isActive
    })

    this.drawerOne = false

    this.setRouteData(this.$route.meta.sideNav)

    // Provjeri scroll mogućnosti nakon što se komponenta učita
    requestAnimationFrame(() => requestAnimationFrame(() => this.checkScrollability()))

    // Dodaj resize listener za ažuriranje scroll indikatora
    window.addEventListener('resize', this.checkScrollability)

    // Listen for sidenav hover block event
    EventBus.$on('block-sidenav-hover-temporarily', this.blockHoverTemporarily)
  },
  beforeDestroy () {
    EventBus.$off('block-sidenav-hover-temporarily', this.blockHoverTemporarily)
    this.$root.$off('eracun-status-changed')
    // Cleanup timeout
    if (this.hoverUnblockTimeout) {
      clearTimeout(this.hoverUnblockTimeout)
      this.hoverUnblockTimeout = null
    }
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout)
    }
    // Ukloni resize listener
    window.removeEventListener('resize', this.checkScrollability)
  }
}
</script>

<style scoped>
/* Transparent overlay to block hover events temporarily */
.hover-block-overlay {
  position: fixed;
  left: 0;
  top: 64px;
  height: calc(100vh - 64px);
  width: 72px;
  z-index: 1000;
  pointer-events: auto;
  background: transparent;
}

.sidebar {
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  width: 72px;
  background: var(--sidebar-bg);
  color: var(--sidebar-text);
  z-index: 100;
  display: flex;
  flex-direction: column;
  transition: background-color 0.3s ease, color 0.3s ease;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.12);
  border-right: 1px solid #EEF2F5;
}

.sidebar-logo-section {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 64px;
  flex-shrink: 0;
  cursor: pointer;
}

.sidebar-logo-img {
  width: 44px;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 188, 212, 0.1));
  transition: filter 0.3s ease, transform 0.3s ease;
}

.sidebar-logo-section:hover .sidebar-logo-img {
  filter: drop-shadow(0 2px 8px rgba(0, 188, 212, 0.3));
  transform: scale(1.05);
}

.sidebar-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding: 4px 0 60px 0;
}

/* Navigation */
.nav-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 4px 24px;
  overflow-y: auto;
  overflow-x: hidden;
}

.nav-section::-webkit-scrollbar {
  width: 0;
}

.nav-item-wrapper {
  position: relative;
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 4px;
  color: var(--sidebar-text);
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  gap: 4px;
}

.nav-item:hover {
  /* background-color: var(--sidebar-bg-hover); */
  color: var(--sidebar-text-active);
}

.nav-item.active {
  /* background-color: var(--sidebar-active-bg); */
  color: var(--sidebar-text-active);
}

.nav-icon {
  font-size: 20px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 42px !important;
  height: 42px !important;
  border-radius: 11px !important;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.12),
    inset 0 1px 1px rgba(255, 255, 255, 0.12),
    inset 0 -1px 1px rgba(0, 0, 0, 0.08) !important;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), filter 0.3s ease !important;
  position: relative !important;
  flex-shrink: 0;
}

.nav-icon::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 11px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.1), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: -1;
}

.nav-item:hover .nav-icon {
  transform: translateY(-3px) scale(1.05);
  filter: brightness(1.15) saturate(1.1);
  box-shadow:
    0 8px 20px rgba(0, 0, 0, 0.25),
    0 4px 12px rgba(0, 0, 0, 0.15),
    inset 0 1px 2px rgba(255, 255, 255, 0.2),
    inset 0 -1px 2px rgba(0, 0, 0, 0.15);
}

.nav-item:hover .nav-icon::after {
  opacity: 1;
}

.nav-item.active .nav-icon {
  filter: brightness(1.2) saturate(1.2);
  box-shadow:
    0 4px 16px rgba(0, 0, 0, 0.3),
    0 2px 8px rgba(0, 0, 0, 0.2),
    inset 0 1px 1px rgba(255, 255, 255, 0.2) !important;
}

.nav-label {
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  line-height: 1.2;
  opacity: 0.85;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-shadow: 0 0 0 transparent;
}

.nav-item:hover .nav-label {
  opacity: 1;
  transform: translateY(-1px);
  text-shadow: 0 2px 8px rgba(0, 179, 190, 0.4);
  font-weight: 700;
  letter-spacing: 0.3px;
}

.nav-item.active .nav-label {
  opacity: 1;
  font-weight: 700;
  text-shadow: 0 2px 12px rgba(97, 84, 164, 0.6);
  letter-spacing: 0.3px;
}

.nav-divider {
  height: 1px;
  background-color: var(--sidebar-border);
  margin: 4px 0;
}

/* Submenu Panel */
.submenu-panel {
  position: fixed;
  left: 72px;
  top: 64px;
  min-width: 220px;
  height: calc(100vh - 64px);
  background: var(--sidebar-bg);
  border-left: 1px solid var(--sidebar-border);
  border-right: 1px solid var(--sidebar-border);
  box-shadow: 4px 0 16px rgba(0, 0, 0, 0.15);
  padding: 24px 0;
  z-index: 999;
  display: flex;
  flex-direction: column;
  transition: opacity 0.2s ease;
  overflow-y: auto;
}

.submenu-header {
  padding: 12px 24px;
  font-size: 13px;
  font-weight: 600;
  color: var(--sidebar-text-active);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--sidebar-border);
  margin-bottom: 8px;
}

.submenu-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
}

/* Multi-column panel */
.submenu-panel--columns {
  left: 84px;
  height: auto;
  overflow: visible;
  border-radius: 16px;
  border: 1px solid #CBD5E1;
  background: #FFFFFF;
  box-shadow: 0 10px 25px -5px rgba(51, 65, 85, 0.12), 0 8px 10px -6px rgba(51, 65, 85, 0.1);
  padding: 24px;
}

.submenu-columns {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.submenu-column {
  display: flex;
  flex-direction: column;
  width: max-content;
  min-width: 230px;
  flex-shrink: 0;
  flex-grow: 0;
  border-right: 1px solid #EEF2F5;
  padding: 0 24px 0 0;
  margin-right: 24px;
}

.submenu-column:last-child {
  border-right: none;
  padding-right: 0;
  margin-right: 0;
}

.submenu-column:only-child {
  width: 300px;
  min-width: 300px;
  border-right: none;
  padding-right: 0;
  margin-right: 0;
}

.submenu-column-title {
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0;
  border-bottom: none;
  margin-bottom: 12px;
}

.submenu-column-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0;
  overflow-y: auto;
  flex: 1;
}

/* Sve stavke u svim panelima */
.submenu-item {
  display: flex !important;
  align-items: center !important;
  padding: 8px 12px !important;
  gap: 12px !important;
  color: #263238 !important;
  border-radius: 8px !important;
  transition: background 0.15s ease, color 0.15s ease !important;
}

/* Hover: tirkizna nijansa */
.submenu-item:hover {
  background: rgba(0, 188, 212, 0.04) !important;
  color: #1E293B !important;
}

/* Label wrap — grupira title + subtitle */
.submenu-label-wrap {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

/* Subtitle ispod labele */
.submenu-subtitle {
  font-size: 13px;
  font-weight: 400;
  color: #607D8B;
  white-space: normal;
  line-height: 1.3;
}

/* Label: tamno sivi, stabilan */
.submenu-item .submenu-label {
  font-size: 14px;
  font-weight: 500;
  color: #263238;
  display: flex;
  flex-direction: column;
}

/* Rezervira prostora za bold+letter-spacing stanje da kolone ne plešu */
.submenu-item .submenu-label::before {
  content: attr(data-text);
  height: 0;
  overflow: hidden;
  visibility: hidden;
  font-weight: 600;
  letter-spacing: 0.3px;
  pointer-events: none;
  user-select: none;
}

.submenu-item:hover .submenu-label {
  font-weight: 600;
  letter-spacing: 0.3px;
  color: #1E293B;
}

/* Badge oko ikone */
.submenu-icon-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  min-width: 42px;
  height: 42px;
  border-radius: 11px;
  flex-shrink: 0;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.12),
    inset 0 1px 1px rgba(255, 255, 255, 0.12),
    inset 0 -1px 1px rgba(0, 0, 0, 0.08);
}

/* Ikona unutar bedža */
.submenu-icon-badge .submenu-icon {
  background: none !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  width: 20px !important;
  min-width: 20px !important;
  height: 20px !important;
  font-size: 20px !important;
  filter: saturate(1.2);
}

/* Hover ikone — bez transformacije, boja ostaje */
.submenu-column .submenu-item:hover .submenu-icon {
  transform: none !important;
  box-shadow: none !important;
  background: none !important;
}

.submenu-item {
  text-decoration: none;
  cursor: pointer;
}

.submenu-item.active {
  color: var(--sidebar-text-active);
}

.submenu-icon {
  font-size: 20px !important;
  min-width: 42px;
  width: 42px;
  height: 42px;
  text-align: center;
  display: flex !important;
  align-items: center;
  justify-content: center;
  border-radius: 11px;
  flex-shrink: 0;
}

.submenu-label {
  white-space: nowrap;
  transition: font-weight 0.1s ease-out, letter-spacing 0.1s ease-out;
}

.submenu-item:hover .submenu-label {
  font-weight: 600;
  letter-spacing: 0.3px;
}

.submenu-item.active .submenu-label {
  font-weight: 600;
  letter-spacing: 0.3px;
}

.submenu-item.disabled {
  opacity: 0.65;
  cursor: not-allowed;
  pointer-events: none;
}

.submenu-item.disabled .submenu-icon {
  opacity: 0.5;
}

.submenu-item.disabled .submenu-label {
  opacity: 0.6;
}

/* Submenu Separator */
.submenu-separator {
  margin: 12px 0;
  padding: 0 4px;
}

.submenu-separator-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--sidebar-text);
  opacity: 0.6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
  padding: 0 12px;
}

.submenu-separator-line {
  height: 1px;
  background-color: var(--sidebar-border);
  opacity: 0.5;
}

/* User Section */
.user-section {
  padding: 0 4px 0 4px;
  border-top: 1px solid var(--sidebar-border);
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  background: var(--sidebar-bg);
}

.logout-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px 4px;
  background-color: transparent;
  color: var(--sidebar-text);
  border: none;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.logout-item:hover {
  /* background-color: var(--sidebar-bg-hover); */
  color: #ef4444;
}

.logout-icon {
  font-size: 20px !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 11px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.05), rgba(0, 0, 0, 0.05));
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.1),
    inset 0 1px 1px rgba(255, 255, 255, 0.1),
    inset 0 -1px 1px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  flex-shrink: 0;
}

.logout-item:hover .logout-icon {
  transform: translateY(-3px) scale(1.05);
  box-shadow:
    0 8px 20px rgba(239, 68, 68, 0.3),
    0 4px 12px rgba(0, 0, 0, 0.2),
    inset 0 1px 2px rgba(255, 255, 255, 0.2),
    inset 0 -1px 2px rgba(0, 0, 0, 0.2);
  background: linear-gradient(145deg, rgba(239, 68, 68, 0.15), rgba(239, 68, 68, 0.08));
}

.logout-label {
  font-size: 10px;
  text-align: center;
  opacity: 0.85;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-shadow: 0 0 0 transparent;
}

.logout-item:hover .logout-label {
  opacity: 1;
  transform: translateY(-1px);
  text-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
  font-weight: 600;
  letter-spacing: 0.3px;
}

/* Scroll Indicators */
.scroll-indicator {
  position: relative;
  width: 100%;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    to bottom,
    var(--sidebar-bg) 0%,
    transparent 100%
  );
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 2;
  flex-shrink: 0;
}

.scroll-indicator.scroll-up {
  background: linear-gradient(
    to bottom,
    var(--sidebar-bg) 0%,
    transparent 100%
  );
  margin-top: 4px;
  margin-bottom: -8px;
}

.scroll-indicator.scroll-down {
  background: linear-gradient(to top, var(--sidebar-bg) 0%, transparent 100%);
  margin-top: -8px;
  margin-bottom: 12px;
}

.scroll-indicator i {
  font-size: 18px;
  color: var(--sidebar-text);
  opacity: 0.7;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.03), rgba(0, 0, 0, 0.03));
  box-shadow:
    0 2px 6px rgba(0, 0, 0, 0.08),
    inset 0 1px 1px rgba(255, 255, 255, 0.05);
}

.scroll-indicator.scroll-up i {
  animation: bounce-up 1s ease-in-out infinite;
}

.scroll-indicator.scroll-down i {
  animation: bounce-down 1s ease-in-out infinite;
}

.scroll-indicator:hover {
  transform: scale(1.05);
}

.scroll-indicator:hover i {
  opacity: 1;
  color: var(--sidebar-text-active);
  background: linear-gradient(145deg, rgba(0, 179, 190, 0.15), rgba(57, 137, 201, 0.1));
  box-shadow:
    0 4px 12px rgba(0, 179, 190, 0.3),
    0 2px 8px rgba(57, 137, 201, 0.2),
    inset 0 1px 1px rgba(255, 255, 255, 0.15);
  text-shadow: 0 2px 8px rgba(0, 179, 190, 0.4);
  animation-duration: 0.5s;
}

@keyframes bounce-up {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-3px);
  }
}

@keyframes bounce-down {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(3px);
  }
}

/* Mobile Drawer Styles */
@media (max-width: 768px) {
  .sidebar.mobile-drawer {
    display: flex;
    position: fixed;
    left: -72px;
    top: 64px;
    width: 72px;
    height: calc(100vh - 64px);
    transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 100;
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.2);
  }

  .sidebar.mobile-drawer.mobile-drawer-open {
    left: 0;
  }

  /* Backdrop */
  .mobile-backdrop {
    position: fixed;
    top: 64px;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 99;
    animation: fadeIn 0.3s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  /* Submenu panel na mobilnim uređajima */
  .sidebar.mobile-drawer .submenu-panel {
    left: 72px;
    width: calc(100vw - 72px);
    max-width: 280px;
  }

  .sidebar.mobile-drawer .submenu-panel--columns {
    width: calc(100vw - 72px);
    max-width: 280px;
    top: 64px !important;
    bottom: auto !important;
    height: calc(100vh - 64px);
    overflow-y: auto;
    overflow-x: hidden;
  }

  .sidebar.mobile-drawer .submenu-columns {
    flex-direction: column;
    overflow-y: auto;
  }

  .sidebar.mobile-drawer .submenu-column {
    border-right: none;
    border-bottom: 1px solid var(--sidebar-border);
    min-width: unset;
  }

  .sidebar.mobile-drawer .submenu-column:last-child {
    border-bottom: none;
  }

  /* Hide submenu panel on mobile when drawer is closed */
  .sidebar.mobile-drawer:not(.mobile-drawer-open) .submenu-panel {
    display: none;
  }
}
</style>

<style>
/* Global override for nav icons - non-scoped to ensure it applies */
.sidebar .nav-icon.mdi::before {
  z-index: 1 !important;
  position: relative;
}

.sidebar .submenu-icon.mdi::before {
  z-index: 1 !important;
  position: relative;
}

.sidebar .logout-icon.mdi::before {
  z-index: 1 !important;
  position: relative;
}
</style>
